import { useEffect, useRef } from "react";
import { socketService } from "@admin/services/socket";
import { useAuth } from "./use-auth";

/**
 * Hook to initialize socket connection when authenticated
 * Should be used at the app's root level to ensure sockets are connected
 * when the user is authenticated
 */
export const useSocketInitialization = () => {
	const { isAuthenticated } = useAuth();
	const initialized = useRef(false);
	const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		// When authenticated and not already initialized
		if (isAuthenticated && !initialized.current) {
			// Clear any disconnect timeouts
			if (disconnectTimeoutRef.current) {
				clearTimeout(disconnectTimeoutRef.current);
				disconnectTimeoutRef.current = null;
			}

			// Initialize socket immediately without delay
			console.log("User authenticated, initializing socket");
			socketService.initialize();
			initialized.current = true;
		} else if (!isAuthenticated && initialized.current) {
			// User logged out, disconnect socket
			console.log("User logged out, disconnecting socket");

			// Immediate disconnect is better for UX
			socketService.disconnect();
			initialized.current = false;
		}

		// Cleanup function
		return () => {
			if (disconnectTimeoutRef.current) {
				clearTimeout(disconnectTimeoutRef.current);
			}
		};
	}, [isAuthenticated]);
};
